import {
  createContext,
  createElement,
  useContext,
  type Context,
  type FC,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import { useStateTools } from './useStateTools'

/**
 * Patch function returned by `useSet<Name>State`. Accepts a partial of the
 * state; only the supplied keys are updated, everything else is preserved.
 */
type Setter<T> = (patch: Partial<T>) => void

/**
 * Read function returned by `useGet<Name>State`. Returns the whole state as
 * it is at the moment it is called.
 */
type Getter<T> = () => T

/**
 * Generated read hooks map: one `use<Capitalize<Key>>` hook per property of
 * the initial state object, each returning the current value of that key.
 *
 * Only string keys are included; symbol keys are dropped.
 */
type StateHooks<T> = {
  [K in keyof T as K extends string
    ? `use${Capitalize<K>}`
    : never]: () => T[K]
}

/**
 * Shape returned by {@link createStateFactory}. Combines:
 * - `${Name}Provider` — the React provider component to mount at the root
 *   of the subtree that needs access to this state
 * - `useSet${Name}State` — hook returning a stable patch function
 * - `useGet${Name}State` — hook returning a stable function that reads the
 *   whole current state
 * - `use<Capitalize<Key>>` — one read hook per key in the initial state
 *
 * Use the `Name` type parameter as a string literal so the property names
 * resolve statically (e.g. `StateFactory<'My', State>`).
 */
export type StateFactory<
  Name extends string,
  T
> = StateHooks<T> &
  Record<`${Name}Provider`, FC<PropsWithChildren>> &
  Record<`useSet${Name}State`, () => Setter<T>> &
  Record<`useGet${Name}State`, () => Getter<T>>

/**
 * Turns a plain state object into a fully-typed React context package:
 * a named Provider, a setter hook, a getter hook, and a read hook per
 * property.
 *
 * Each property gets its *own* React context internally. Consumers that
 * only read one key will therefore only re-render when that key changes,
 * rather than on every state update.
 *
 * @param name    Pascal-cased prefix used to name the generated members.
 *                Pass it as a string literal so the return type is precise.
 * @param initial Initial state object. Used once on mount; later changes to
 *                the object itself are ignored — call the setter to update.
 *
 * @example
 * ```tsx
 * interface State {
 *   title: string
 *   count: number
 * }
 *
 * const { MyProvider, useSetMyState, useTitle, useCount } =
 *   createStateFactory('My', { title: 'foo', count: 1 } satisfies State)
 *
 * function Screen() {
 *   const title = useTitle()       // re-renders only on title changes
 *   const setMyState = useSetMyState()
 *   return (
 *     <button onClick={() => setMyState({ title: 'bar' })}>
 *       {title}
 *     </button>
 *   )
 * }
 *
 * function App() {
 *   return (
 *     <MyProvider>
 *       <Screen />
 *     </MyProvider>
 *   )
 * }
 * ```
 *
 * @remarks
 * - Read hooks used outside `${Name}Provider` resolve to the initial value.
 * - The setter used outside the Provider is a no-op (does not throw).
 * - The setter reference is stable across re-renders — safe in effect deps.
 * - `useGet${Name}State` does not subscribe to the state: the component
 *   calling it will not re-render when the state changes. Call the returned
 *   function inside callbacks and effects to read the latest values; use the
 *   per-key read hooks for values you render.
 * - The getter used outside the Provider returns `initial`.
 * - The getter reference is stable across re-renders — safe in effect deps.
 * - Because the Provider owns the state, unmounting it resets the state on
 *   next mount back to `initial`.
 */
export function createStateFactory<
  Name extends string,
  T extends Record<string, unknown>
>(name: Name, initial: T): StateFactory<Name, T> {
  const keys = Object.keys(initial) as Array<keyof T & string>
  const contexts: Record<string, Context<unknown>> = {}
  const api: Record<string, unknown> = {}

  for (const key of keys) {
    const context = createContext<unknown>(initial[key])
    context.displayName = `${key}`
    contexts[key] = context
    const hookName = `use${key
      .charAt(0)
      .toUpperCase()}${key.slice(1)}`
    api[hookName] = makeUseContextValue(context)
  }

  const noopSetter: Setter<T> = () => {}
  const SetterContext = createContext<Setter<T>>(noopSetter)
  api[`useSet${name}State`] = makeUseContextValue(SetterContext)

  const initialGetter: Getter<T> = () => initial
  const GetterContext = createContext<Getter<T>>(initialGetter)
  api[`useGet${name}State`] = makeUseContextValue(GetterContext)

  const Provider: FC<PropsWithChildren> = ({ children }) => {
    const [state, getState, setState] = useStateTools<T>(initial)
    const wrapped = keys.reduceRight<ReactNode>(
      (acc, key) =>
        createElement(
          contexts[key].Provider,
          { value: state[key] },
          acc
        ),
      children
    )
    return createElement(
      SetterContext.Provider,
      { value: setState },
      createElement(
        GetterContext.Provider,
        { value: getState },
        wrapped
      )
    )
  }
  Provider.displayName = `${name}Provider`

  api[`${name}Provider`] = Provider
  return api as StateFactory<Name, T>
}

/**
 * Wraps `useContext(context)` in a named hook function so that the
 * react-hooks ESLint rule recognises the dynamic assignment
 * (`api[hookName] = ...`) as a valid hook — plain arrow functions assigned
 * to a computed key are flagged by `rules-of-hooks`.
 */
function makeUseContextValue<V>(context: Context<V>): () => V {
  return function useContextValue() {
    return useContext(context)
  }
}
